import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Filter, FileSpreadsheet, ClipboardList, BarChart3 } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MESES = [
  { value: 'Todos', label: 'Todos los meses' },
  { value: '01', label: 'Enero' }, { value: '02', label: 'Febrero' }, { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Mayo' }, { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' }, { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' },
];

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const getStatusBadge = (estado) => {
  const badges = {
    Pendiente: 'bg-indigo-100 text-indigo-800',
    'En proceso': 'bg-blue-100 text-blue-800',
    'En Proceso': 'bg-blue-100 text-blue-800',
    Realizado: 'bg-emerald-100 text-emerald-800',
    Finalizado: 'bg-emerald-100 text-emerald-800',
    Vencido: 'bg-red-100 text-red-800',
  };
  return badges[estado] || 'bg-slate-100 text-slate-800';
};

const exportToExcel = (data, headers, filename) => {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
  XLSX.writeFile(wb, filename);
};

const exportToPDF = (title, subtitle, headers, rows) => {
  const doc = new jsPDF('landscape');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('AS CONSULTING SAS', 14, 15);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Firma Contable', 14, 21);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 32);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle, 14, 38);
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 43,
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [31, 56, 100], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text(`Pagina ${i} de ${pageCount}`, doc.internal.pageSize.getWidth() - 30, doc.internal.pageSize.getHeight() - 10);
  }
  return doc;
};

// ==================== Tab: Vencimientos ====================
const VencimientosReport = () => {
  const [taxTypes, setTaxTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedType, setSelectedType] = useState('Todos');
  const [selectedClient, setSelectedClient] = useState('Todos');
  const [selectedStatus, setSelectedStatus] = useState('Todos');
  const [selectedMonth, setSelectedMonth] = useState('Todos');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/tax-types`).then(r => setTaxTypes(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/clients`).then(r => setClients(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedType !== 'Todos') params.append('tipo_impuesto', selectedType);
      if (selectedStatus !== 'Todos') params.append('estado', selectedStatus);
      if (selectedClient !== 'Todos') params.append('client_id', selectedClient);
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/by-tax-type?${params}`);
      const filtered = selectedMonth === 'Todos' ? data : data.filter(d => d.fecha_vencimiento?.substring(5, 7) === selectedMonth);
      setReportData(filtered);
      setGenerated(true);
    } catch { } finally { setLoading(false); }
  };

  const doExportPDF = () => {
    const doc = exportToPDF(
      'Reporte de Vencimientos',
      `${reportData.length} registros | ${new Date().toLocaleDateString('es-CO')}`,
      ['#', 'Cliente', 'NIT', 'Tipo Impuesto', 'Periodo', 'Fecha', 'Estado', 'Responsable'],
      reportData.map((d, i) => [i + 1, d.client_name, d.client_nit, d.tipo_display || d.tipo_impuesto, d.periodo_gravable, d.fecha_vencimiento, d.estado_display || d.estado, d.responsable_name])
    );
    doc.save(`Vencimientos_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const doExportExcel = () => {
    exportToExcel(
      reportData.map((d, i) => [i + 1, d.client_name, d.client_nit, d.tipo_display || d.tipo_impuesto, d.periodo_gravable, d.fecha_vencimiento, d.estado_display || d.estado, d.responsable_name]),
      ['#', 'Cliente', 'NIT', 'Tipo Impuesto', 'Periodo', 'Fecha', 'Estado', 'Responsable'],
      `Vencimientos_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <select value={selectedClient} onChange={(e) => { setSelectedClient(e.target.value); setGenerated(false); }}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="report-client-select">
          <option value="Todos">Todos los clientes</option>
          {clients.map(c => <option key={c._id} value={c._id}>{c.razon_social || c.nit}</option>)}
        </select>
        <select value={selectedType} onChange={(e) => { setSelectedType(e.target.value); setGenerated(false); }}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="report-tax-type-select">
          <option value="Todos">Todos los impuestos</option>
          {taxTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setGenerated(false); }}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="report-status-select">
          <option value="Todos">Todos</option>
          <option value="Pendiente">Pendiente</option>
          <option value="Realizado">Realizado</option>
          <option value="Vencido">Vencido</option>
        </select>
        <select value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setGenerated(false); }}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="report-month-select">
          {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <button onClick={generate} disabled={loading}
          className="px-4 py-2 bg-[#1F3864] hover:bg-[#172b4d] disabled:bg-slate-300 text-white rounded-md text-sm font-medium" data-testid="generate-report-btn">
          {loading ? 'Generando...' : 'Generar'}
        </button>
      </div>

      {generated && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-600">{reportData.length} registros</span>
            <div className="flex gap-2">
              <button onClick={doExportPDF} disabled={!reportData.length}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white rounded-md text-xs font-medium" data-testid="export-pdf-btn">
                <Download className="w-3.5 h-3.5" />PDF
              </button>
              <button onClick={doExportExcel} disabled={!reportData.length}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-md text-xs font-medium" data-testid="export-excel-btn">
                <FileSpreadsheet className="w-3.5 h-3.5" />Excel
              </button>
            </div>
          </div>
          {reportData.length === 0 ? <p className="text-sm text-slate-500 text-center py-6">Sin resultados</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left">#</th><th className="py-2 px-3 text-left">Cliente</th><th className="py-2 px-3 text-left">NIT</th>
                    <th className="py-2 px-3 text-left">Tipo</th><th className="py-2 px-3 text-left">Periodo</th><th className="py-2 px-3 text-left">Fecha</th>
                    <th className="py-2 px-3 text-left">Estado</th><th className="py-2 px-3 text-left">Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((d, i) => (
                    <tr key={d._id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-500">{i + 1}</td>
                      <td className="py-2 px-3 font-medium text-slate-800">{d.client_name}</td>
                      <td className="py-2 px-3 text-slate-600">{d.client_nit}</td>
                      <td className="py-2 px-3 text-slate-600">{d.tipo_display || d.tipo_impuesto}</td>
                      <td className="py-2 px-3 text-slate-600">{d.periodo_gravable}</td>
                      <td className="py-2 px-3 font-medium">{d.fecha_vencimiento}</td>
                      <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(d.estado_display || d.estado)}`}>{d.estado_display || d.estado}</span></td>
                      <td className="py-2 px-3 text-slate-600">{d.responsable_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== Tab: Obligaciones por Cliente ====================
const ObligacionesReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/reports/obligaciones-cliente`);
      setData(d.filter(c => c.total_obligaciones > 0));
      setGenerated(true);
    } catch { } finally { setLoading(false); }
  };

  const doExportPDF = () => {
    const rows = [];
    data.forEach(c => {
      c.obligaciones.forEach((o, i) => {
        rows.push([i === 0 ? c.client_name : '', i === 0 ? c.nit : '', o.tipo_display, o.periodicidad || '']);
      });
    });
    const doc = exportToPDF('Obligaciones por Cliente', `${data.length} clientes | ${new Date().toLocaleDateString('es-CO')}`,
      ['Cliente', 'NIT', 'Obligación', 'Periodicidad'], rows);
    doc.save(`Obligaciones_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const doExportExcel = () => {
    const rows = [];
    data.forEach(c => c.obligaciones.forEach(o => rows.push([c.client_name, c.nit, o.tipo_display, o.periodicidad || ''])));
    exportToExcel(rows, ['Cliente', 'NIT', 'Obligación', 'Periodicidad'], `Obligaciones_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <button onClick={generate} disabled={loading}
          className="px-4 py-2 bg-[#1F3864] hover:bg-[#172b4d] disabled:bg-slate-300 text-white rounded-md text-sm font-medium" data-testid="gen-obligaciones-btn">
          {loading ? 'Cargando...' : 'Generar Reporte'}
        </button>
        {generated && data.length > 0 && (
          <>
            <button onClick={doExportPDF} className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium"><Download className="w-3.5 h-3.5" />PDF</button>
            <button onClick={doExportExcel} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium"><FileSpreadsheet className="w-3.5 h-3.5" />Excel</button>
          </>
        )}
      </div>
      {generated && (
        <div className="space-y-3">
          {data.map(c => (
            <div key={c.client_id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">{c.client_name}</h3>
                  <p className="text-xs text-slate-500">NIT: {c.nit}</p>
                </div>
                <span className="text-xs font-bold bg-[#1F3864] text-white px-2 py-0.5 rounded-full">{c.total_obligaciones}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.obligaciones.map((o, i) => (
                  <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{o.tipo_display}{o.periodicidad ? ` (${o.periodicidad})` : ''}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== Tab: Avance Cierre Mensual ====================
const AvanceCierreReport = () => {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const mes = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const generate = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/reports/avance-cierre?mes=${mes}`);
      setData(d);
      setGenerated(true);
    } catch { } finally { setLoading(false); }
  };

  const doExportPDF = () => {
    const doc = exportToPDF(
      `Avance Cierre — ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`,
      `${data.length} clientes | ${new Date().toLocaleDateString('es-CO')}`,
      ['#', 'Cliente', 'Responsable', 'Total', 'Finalizado', 'En Proceso', 'Pendiente', '% Avance'],
      data.map((d, i) => [i + 1, d.client_name, d.responsable_name, d.total, d.finalizado, d.en_proceso, d.pendiente, `${d.pct_finalizado}%`])
    );
    doc.save(`Avance_${mes}.pdf`);
  };

  const doExportExcel = () => {
    exportToExcel(
      data.map((d, i) => [i + 1, d.client_name, d.responsable_name, d.total, d.finalizado, d.en_proceso, d.pendiente, d.pct_finalizado]),
      ['#', 'Cliente', 'Responsable', 'Total', 'Finalizado', 'En Proceso', 'Pendiente', '% Avance'],
      `Avance_${mes}.xlsx`
    );
  };

  const globalTotal = data.reduce((s, d) => s + d.total, 0);
  const globalFin = data.reduce((s, d) => s + d.finalizado, 0);
  const globalPct = globalTotal ? Math.round(globalFin / globalTotal * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <select value={selectedMonth} onChange={(e) => { setSelectedMonth(+e.target.value); setGenerated(false); }}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm">
          {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={selectedYear} onChange={(e) => { setSelectedYear(+e.target.value); setGenerated(false); }}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm">
          {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={generate} disabled={loading}
          className="px-4 py-2 bg-[#1F3864] hover:bg-[#172b4d] disabled:bg-slate-300 text-white rounded-md text-sm font-medium" data-testid="gen-avance-btn">
          {loading ? 'Cargando...' : 'Generar'}
        </button>
        {generated && data.length > 0 && (
          <>
            <button onClick={doExportPDF} className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium"><Download className="w-3.5 h-3.5" />PDF</button>
            <button onClick={doExportExcel} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium"><FileSpreadsheet className="w-3.5 h-3.5" />Excel</button>
          </>
        )}
        {generated && data.length > 0 && (
          <div className="ml-auto text-right">
            <p className="text-xs text-slate-500">Avance global</p>
            <p className="text-xl font-bold text-[#1F3864]">{globalPct}%</p>
          </div>
        )}
      </div>
      {generated && data.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No hay cierre generado para este mes</p>}
      {generated && data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b">
              <tr>
                <th className="py-2 px-3 text-left">#</th><th className="py-2 px-3 text-left">Cliente</th><th className="py-2 px-3 text-left">Responsable</th>
                <th className="py-2 px-3 text-center">Total</th><th className="py-2 px-3 text-center">Finalizado</th><th className="py-2 px-3 text-center">En Proceso</th>
                <th className="py-2 px-3 text-center">Pendiente</th><th className="py-2 px-3 text-left">Avance</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 text-slate-500">{i + 1}</td>
                  <td className="py-2 px-3 font-medium text-slate-800">{d.client_name}</td>
                  <td className="py-2 px-3 text-slate-600">{d.responsable_name}</td>
                  <td className="py-2 px-3 text-center">{d.total}</td>
                  <td className="py-2 px-3 text-center text-emerald-700 font-medium">{d.finalizado}</td>
                  <td className="py-2 px-3 text-center text-blue-700">{d.en_proceso}</td>
                  <td className="py-2 px-3 text-center text-slate-500">{d.pendiente}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${d.pct_finalizado}%` }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700 w-8">{d.pct_finalizado}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== Main Reports Page ====================
const Reports = () => {
  const [activeTab, setActiveTab] = useState('vencimientos');

  const tabs = [
    { id: 'vencimientos', label: 'Vencimientos', icon: FileText },
    { id: 'obligaciones', label: 'Obligaciones por Cliente', icon: ClipboardList },
    { id: 'avance', label: 'Avance Cierre Mensual', icon: BarChart3 },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Reportes</h1>
        <p className="text-sm text-slate-700 leading-relaxed mt-2">Genera y exporta reportes en PDF y Excel</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="report-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
              activeTab === tab.id ? 'border-[#1F3864] text-[#1F3864]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`} data-testid={`report-tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        {activeTab === 'vencimientos' && <VencimientosReport />}
        {activeTab === 'obligaciones' && <ObligacionesReport />}
        {activeTab === 'avance' && <AvanceCierreReport />}
      </div>
    </div>
  );
};

export default Reports;
